import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, Loader2, ShieldCheck, Check, Globe2,
  Stethoscope, Pill, Users, Video, ClipboardCheck, BarChart3, Calculator,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import PrivacyLink from "../components/PrivacyLink";
import { SERVICE_LIST, usePublicConfig, calcService, WTE_HOURS, fmt } from "../components/calculators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#FAFAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-sm text-[#0B192C] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#10B981] focus:bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const COUNTRIES = [
  {
    key: "uae",
    name: "United Arab Emirates",
    formValue: "United Arab Emirates",
    city: "Dubai",
    currency: "AED",
    flag: "https://flagcdn.com/w80/ae.png",
    image: "/assets/skyline-dubai.png",
    color: "#10B981",
    title: "Clinical Pharmacy & Healthcare Partnerships",
    intro: "NCS welcomes discussions with Dubai-based healthcare providers interested in:",
    services: [
      "Clinical pharmacist workforce solutions",
      "Medicines optimisation",
      "Medication review programmes",
      "Polypharmacy and deprescribing support",
      "Prescribing quality improvement",
      "Clinical pharmacy service development",
      "Remote clinical pharmacy support where legally authorised",
      "Healthcare consultancy and quality improvement projects",
    ],
    regulatory: [
      "The Dubai Health Authority (DHA) is a relevant professional regulator for Dubai healthcare services.",
      "Pharmacists and other healthcare professionals must satisfy the applicable professional registration, licensing and scope-of-practice requirements.",
      "DHA's Sheryan system supports professional eligibility assessment, registration and licence activation.",
      "Remote patient-facing services require separate consideration of telehealth standards, healthcare facility authorisation and professional scope.",
    ],
    cta: "Explore a Dubai Partnership",
  },
  {
    key: "qatar",
    name: "Qatar",
    formValue: "Qatar",
    city: "Doha",
    currency: "QAR",
    flag: "https://flagcdn.com/w80/qa.png",
    image: "/assets/skyline-doha.png",
    color: "#06B6D4",
    title: "Clinical Pharmacy Workforce & Service Development",
    intro: "NCS is exploring partnerships with healthcare providers and organisations in Doha and across Qatar. Potential areas of collaboration:",
    services: [
      "Clinical pharmacist workforce support",
      "Pharmacy service development",
      "Medicines optimisation programmes",
      "Medication safety audits",
      "Polypharmacy reviews",
      "Clinical quality improvement",
      "Workforce planning",
      "Remote clinical pharmacy support where authorised",
      "Bespoke healthcare projects",
    ],
    regulatory: [
      "Healthcare professional licensing in Qatar is overseen by the Ministry of Public Health's Department of Healthcare Professions (DHP).",
      "Relevant professionals must satisfy registration and licensing requirements.",
      "Remote clinical services must comply with Qatar's telemedicine and professional practice requirements.",
    ],
    cta: "Explore a Qatar Partnership",
  },
  {
    key: "oman",
    name: "Sultanate of Oman",
    formValue: "Oman",
    city: "Muscat",
    currency: "OMR",
    flag: "https://flagcdn.com/w80/om.png",
    image: "/assets/skyline-muscat.png",
    color: "#EF4444",
    title: "Clinical Pharmacy & Medicines Optimisation Partnerships",
    intro: "NCS welcomes opportunities to collaborate with healthcare organisations in Oman. Potential services:",
    services: [
      "Clinical pharmacy consultancy",
      "Medicines optimisation",
      "Medication review programmes",
      "Prescribing safety audits",
      "Pharmacy workforce development",
      "Clinical training and service improvement",
      "Remote consultancy",
      "Clinical pharmacy workforce partnerships",
      "Healthcare quality improvement projects",
    ],
    regulatory: [
      "Professional registration and pharmacy licensing are administered through the relevant Omani Ministry of Health processes.",
      "Any clinical service must be delivered by appropriately authorised professionals under the relevant local requirements.",
    ],
    cta: "Explore an Oman Partnership",
  },
];

const SERVICES = [
  { icon: Stethoscope, color: "#10B981", title: "Clinical Pharmacy Consultancy", formValue: "Clinical pharmacy consultancy",
    text: "Support with designing and improving clinical pharmacy services, workforce models, clinical governance and medication safety processes." },
  { icon: Pill, color: "#06B6D4", title: "Medicines Optimisation Programmes", formValue: "Medicines optimisation",
    text: "Targeted projects focused on polypharmacy, prescribing safety, medication appropriateness and evidence-based care." },
  { icon: Users, color: "#F59E0B", title: "Pharmacist Workforce Solutions", formValue: "Pharmacist workforce",
    text: "Support with sourcing, planning and deploying appropriately qualified and locally licensed clinical pharmacists." },
  { icon: Video, color: "#EF4444", title: "Remote Clinical Pharmacy Partnerships", formValue: "Remote pharmacy support",
    text: "Explore remote clinical services where permitted by the relevant jurisdiction, healthcare facility authorisations, professional licensing requirements and data protection rules." },
  { icon: ClipboardCheck, color: "#10B981", title: "Medication Review Projects", formValue: "Medication reviews",
    text: "Defined programmes for medication review, prescribing quality improvement and clinical pharmacy service development." },
  { icon: BarChart3, color: "#06B6D4", title: "Healthcare Workforce Planning", formValue: "Clinical service development",
    text: "Support organisations with estimating pharmacist hours, staffing requirements, project timelines and workforce capacity." },
];

const STEPS = [
  { n: 1, title: "Submit an Enquiry", text: "Tell us about your organisation and clinical service requirements." },
  { n: 2, title: "Initial Consultation", text: "Our team will discuss the project scope, expected outcomes, workforce requirements and delivery model." },
  { n: 3, title: "Regulatory & Operational Review", text: "We will establish the applicable licensing, professional registration, clinical governance and local partnership requirements." },
  { n: 4, title: "Service Proposal", text: "NCS will prepare a tailored proposal setting out the proposed service, staffing model, indicative cost and delivery arrangements." },
  { n: 5, title: "Partnership & Delivery", text: "Following appropriate approvals and contractual arrangements, the agreed service can proceed." },
];

const ORG_TYPES = ["Hospital", "Medical centre", "Primary care provider", "Pharmacy group",
  "Healthcare investor", "Healthcare commissioner", "Healthcare workforce provider", "Other"];
const SERVICE_OPTIONS = ["Clinical pharmacy consultancy", "Medicines optimisation", "Pharmacist workforce",
  "Remote pharmacy support", "Medication reviews", "Clinical service development", "Other"];
const DELIVERY_OPTIONS = ["Remote", "On-site", "Hybrid", "To be discussed"];

/* ------------------------------ Calculator ------------------------------ */
const IntlCalculator = ({ onAttach }) => {
  const config = usePublicConfig();
  const [calc, setCalc] = useState({
    country: "uae", service: "smr", patients: 500, weeks: 12,
    pharmacists: 1, hoursOverride: "", delivery: "To be discussed",
    licence: "", start: "",
  });
  const upd = (k) => (e) => setCalc((c) => ({ ...c, [k]: e.target.value }));

  const country = COUNTRIES.find((c) => c.key === calc.country);
  const result = useMemo(() => {
    const patients = Number(calc.patients) || 0;
    const weeks = Number(calc.weeks) || 0;
    const base = calcService(config, { serviceKey: calc.service, patients, weeks, teamPref: "solo" });
    const override = Number(calc.hoursOverride) || 0;
    const hours = override > 0 ? override : base.pharmacistHours;
    const weeklyHours = weeks > 0 ? hours / weeks : 0;
    const fte = weeks > 0 ? hours / (WTE_HOURS * weeks) : 0;
    const numPh = Number(calc.pharmacists) || 0;
    const perPharmacist = numPh > 0 ? weeklyHours / numPh : null;
    const rate = Number(config.international_rates?.[country.currency]) || 0;
    const cost = rate > 0 ? hours * rate : null;
    return { hours, weeklyHours, fte, numPh, perPharmacist, rate, cost, weeks };
  }, [calc, config, country]);

  const serviceLabel = SERVICE_LIST.find((s) => s.key === calc.service)?.label || calc.service;

  const attach = () => {
    const items = [
      { label: "Calculator country", value: `${country.name} (${country.city})` },
      { label: "Clinical service", value: serviceLabel },
      { label: "Patients / reviews", value: String(calc.patients) },
      { label: "Project duration", value: `${calc.weeks} weeks` },
      { label: "Indicative pharmacist hours", value: `${fmt(result.hours, 0)} hrs` },
      { label: "Full-time equivalent", value: `${fmt(result.fte, 2)} WTE` },
      { label: "Delivery preference", value: calc.delivery },
    ];
    if (result.cost) items.push({ label: `Indicative cost (${country.currency})`, value: `${country.currency} ${Math.round(result.cost).toLocaleString("en-GB")}` });
    if (calc.licence) items.push({ label: "Licence / registration required", value: calc.licence });
    if (calc.start) items.push({ label: "Preferred start", value: calc.start });
    onAttach(items);
    scrollToId("partner");
    toast.success("Estimate attached to the partnership enquiry form below.");
  };

  return (
    <div data-testid="intl-calculator" className="bg-white border border-[#E2E8F0] rounded-[2rem] p-7 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="calc-country" className={labelCls}>Country</label>
            <select id="calc-country" data-testid="calc-select-country" value={calc.country} onChange={upd("country")} className={inputCls}>
              {COUNTRIES.map((c) => <option key={c.key} value={c.key}>{`${c.name} — ${c.city}`}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="calc-service" className={labelCls}>Clinical service required</label>
            <select id="calc-service" data-testid="calc-select-service" value={calc.service} onChange={upd("service")} className={inputCls}>
              {SERVICE_LIST.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="calc-patients" className={labelCls}>Patients / reviews</label>
            <input id="calc-patients" data-testid="calc-input-patients" type="number" min="0"
              value={calc.patients} onChange={upd("patients")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="calc-weeks" className={labelCls}>Project duration (weeks)</label>
            <input id="calc-weeks" data-testid="calc-input-weeks" type="number" min="1"
              value={calc.weeks} onChange={upd("weeks")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="calc-pharmacists" className={labelCls}>Number of pharmacists</label>
            <input id="calc-pharmacists" data-testid="calc-input-pharmacists" type="number" min="0"
              value={calc.pharmacists} onChange={upd("pharmacists")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="calc-hours" className={labelCls}>Pharmacist hours required <span className="normal-case font-medium text-[#94A3B8]">(optional override)</span></label>
            <input id="calc-hours" data-testid="calc-input-hours" type="number" min="0" placeholder="Auto-estimated"
              value={calc.hoursOverride} onChange={upd("hoursOverride")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="calc-delivery" className={labelCls}>Delivery preference</label>
            <select id="calc-delivery" data-testid="calc-select-delivery" value={calc.delivery} onChange={upd("delivery")} className={inputCls}>
              {DELIVERY_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="calc-start" className={labelCls}>Preferred start date</label>
            <input id="calc-start" data-testid="calc-input-start" type="date"
              value={calc.start} onChange={upd("start")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="calc-licence" className={labelCls}>Required professional licence / registration</label>
            <input id="calc-licence" data-testid="calc-input-licence" placeholder="e.g. DHA pharmacist licence, DHP registration"
              value={calc.licence} onChange={upd("licence")} className={inputCls} />
          </div>
        </div>

        <div data-testid="calc-results" className="bg-[#0B192C] rounded-3xl p-7 sm:p-8 flex flex-col">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#10B981]">
            <Calculator size={14} /> Indicative estimate — {country.name}
          </p>
          <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-white/45">Pharmacist hours</p>
              <p data-testid="calc-result-hours" className="mt-1 font-display text-2xl font-extrabold text-white">{fmt(result.hours, 0)} hrs</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-white/45">Hours per week</p>
              <p data-testid="calc-result-weekly" className="mt-1 font-display text-2xl font-extrabold text-white">{fmt(result.weeklyHours, 1)}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-white/45">FTE requirement</p>
              <p data-testid="calc-result-fte" className="mt-1 font-display text-2xl font-extrabold text-white">{fmt(result.fte, 2)} WTE</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-white/45">Indicative cost</p>
              {result.cost ? (
                <p data-testid="calc-result-cost" className="mt-1 font-display text-2xl font-extrabold text-[#10B981]">
                  {country.currency} {Math.round(result.cost).toLocaleString("en-GB")}
                </p>
              ) : (
                <p data-testid="calc-pricing-note" className="mt-1 text-sm font-semibold text-white/70 leading-snug">
                  Provided on enquiry — no approved {country.currency} rate published yet
                </p>
              )}
            </div>
          </div>
          {result.perPharmacist !== null && result.perPharmacist > WTE_HOURS && (
            <p data-testid="calc-capacity-warning" className="mt-5 text-xs leading-relaxed bg-[#F59E0B]/15 text-[#FCD34D] rounded-xl px-4 py-3">
              This workload averages {fmt(result.perPharmacist, 1)} hrs/week per pharmacist — above a standard
              {" "}{WTE_HOURS}-hour week. Consider more pharmacists or a longer duration.
            </p>
          )}
          <p className="mt-5 text-[11px] leading-relaxed text-white/40">
            Estimates are indicative only, based on NCS standard service times. Delivery is subject to local
            regulatory approval, professional licensing, facility authorisation and contract. Indicative costs
            are shown only where NCS has approved commercial rates for the selected country.
          </p>
          <button
            data-testid="calc-attach-btn"
            onClick={attach}
            className="group mt-auto pt-6 inline-flex items-center gap-2.5 text-sm font-bold text-[#10B981] hover:text-white transition-colors"
          >
            Attach this estimate to the partnership enquiry
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------- Partnership form ---------------------------- */
const PartnerForm = ({ country, service, calcSummary }) => {
  const empty = {
    full_name: "", organisation: "", country: "United Arab Emirates", city: "",
    org_type: "Hospital", email: "", phone: "", service: SERVICE_OPTIONS[0],
    pharmacists: "", volume: "", preference: "To be discussed",
    start_date: "", additional: "", website: "",
  };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (country) setForm((f) => ({ ...f, country })); }, [country]);
  useEffect(() => { if (service) setForm((f) => ({ ...f, service })); }, [service]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const details = [
      { label: "Country", value: form.country },
      { label: "City", value: form.city },
      ...(form.pharmacists ? [{ label: "Pharmacists required", value: form.pharmacists }] : []),
      ...(form.volume ? [{ label: "Estimated patient volume", value: form.volume }] : []),
      { label: "Remote / on-site preference", value: form.preference },
      ...calcSummary,
    ];
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.full_name,
        organisation: form.organisation,
        org_type: form.org_type,
        email: form.email,
        phone: form.phone,
        requirement: `International — ${form.service} (${form.country})`,
        start_date: form.start_date || null,
        additional: form.additional || null,
        journey: "international",
        website: form.website,
        calculator: details,
      });
      setSent(true);
      toast.success("Enquiry submitted — our international partnerships team will respond shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="partner-success" className="bg-white border border-[#10B981]/30 rounded-[2rem] p-10 sm:p-14 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Partnership enquiry received</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you, {form.full_name.split(" ")[0]}. Your enquiry is with our international partnerships
          team — a real person will review it and respond, usually within one working day. A confirmation
          email is on its way to {form.email}.
        </p>
        <button data-testid="partner-new-button" onClick={() => { setSent(false); setForm(empty); }}
          className="mt-8 text-sm font-bold text-[#0B192C] border-b-2 border-[#10B981] pb-1 hover:text-[#10B981] transition-colors">
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="partner-form"
      className="bg-white border border-[#E2E8F0] rounded-[2rem] p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <h2 className="font-display text-2xl font-bold text-[#0B192C]">Partner With NCS International</h2>
      <p className="mt-2 text-sm text-[#64748B]">
        Tell us about your organisation and requirements — fields marked * are required.
      </p>

      {calcSummary.length > 0 && (
        <div data-testid="partner-calc-summary" className="mt-6 bg-[#0B192C] rounded-2xl p-6">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#10B981]">
            <Calculator size={14} /> Your calculator estimate (indicative)
          </p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
            {calcSummary.map((c) => (
              <div key={c.label}>
                <p className="text-[11px] uppercase tracking-wider text-white/45">{c.label}</p>
                <p className="text-sm font-bold text-white mt-0.5">{c.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="pt-name" className={labelCls}>Full name *</label>
          <input id="pt-name" data-testid="partner-input-name" required minLength={2}
            value={form.full_name} onChange={upd("full_name")} placeholder="Dr. Ahmed Al Mansoori" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pt-org" className={labelCls}>Organisation name *</label>
          <input id="pt-org" data-testid="partner-input-organisation" required minLength={2}
            value={form.organisation} onChange={upd("organisation")} placeholder="Gulf Medical Centre" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pt-country" className={labelCls}>Country *</label>
          <select id="pt-country" data-testid="partner-select-country" value={form.country} onChange={upd("country")} className={inputCls}>
            {["United Arab Emirates", "Qatar", "Oman", "Other"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="pt-city" className={labelCls}>City *</label>
          <input id="pt-city" data-testid="partner-input-city" required minLength={2}
            value={form.city} onChange={upd("city")} placeholder="Dubai" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pt-orgtype" className={labelCls}>Organisation type *</label>
          <select id="pt-orgtype" data-testid="partner-select-orgtype" value={form.org_type} onChange={upd("org_type")} className={inputCls}>
            {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="pt-service" className={labelCls}>Service required *</label>
          <select id="pt-service" data-testid="partner-select-service" value={form.service} onChange={upd("service")} className={inputCls}>
            {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="pt-email" className={labelCls}>Email address *</label>
          <input id="pt-email" data-testid="partner-input-email" type="email" required
            value={form.email} onChange={upd("email")} placeholder="a.almansoori@example.com" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pt-phone" className={labelCls}>Contact number *</label>
          <input id="pt-phone" data-testid="partner-input-phone" type="tel" required minLength={5}
            value={form.phone} onChange={upd("phone")} placeholder="+971 50 123 4567" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pt-pharmacists" className={labelCls}>Number of pharmacists required <span className="normal-case font-medium text-[#94A3B8]">(if applicable)</span></label>
          <input id="pt-pharmacists" data-testid="partner-input-pharmacists" type="number" min="0"
            value={form.pharmacists} onChange={upd("pharmacists")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="pt-volume" className={labelCls}>Project size / estimated patient volume</label>
          <input id="pt-volume" data-testid="partner-input-volume"
            value={form.volume} onChange={upd("volume")} placeholder="e.g. 2,000 medication reviews per year" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pt-preference" className={labelCls}>Remote, on-site or hybrid</label>
          <select id="pt-preference" data-testid="partner-select-preference" value={form.preference} onChange={upd("preference")} className={inputCls}>
            {DELIVERY_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="pt-start" className={labelCls}>Preferred start date</label>
          <input id="pt-start" data-testid="partner-input-startdate" type="date"
            value={form.start_date} onChange={upd("start_date")} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pt-additional" className={labelCls}>Additional requirements</label>
          <textarea id="pt-additional" data-testid="partner-input-additional" rows={4}
            value={form.additional} onChange={upd("additional")}
            placeholder="Facilities covered, timelines, governance requirements, existing pharmacy services..."
            className={`${inputCls} resize-none`} />
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="pt-web">Website</label>
          <input id="pt-web" data-testid="partner-honeypot" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={upd("website")} />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 bg-[#F1F5F9] rounded-2xl px-5 py-4">
        <ShieldCheck size={17} className="text-[#10B981] shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-[#475569]">
          Please do not include identifiable patient information. All international services are subject to
          local regulatory approval, professional licensing, healthcare facility authorisation, contractual
          arrangements and operational feasibility. Read our <PrivacyLink testid="partner-privacy-link" />.
        </p>
      </div>

      <button type="submit" disabled={sending} data-testid="partner-submit-btn"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed">
        {sending ? (<>Submitting <Loader2 size={16} className="animate-spin" /></>)
          : (<>Submit partnership enquiry <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
      </button>
    </form>
  );
};

/* --------------------------- Professionals form --------------------------- */
const ProfessionalForm = () => {
  const empty = {
    full_name: "", email: "", country_of_residence: "", role: "Clinical Pharmacist",
    regulator: "", registration_status: "Fully registered", interests: [],
    experience_years: "3–5 years", preference: "Either", website: "",
  };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (c) => setForm((f) => ({
    ...f, interests: f.interests.includes(c) ? f.interests.filter((x) => x !== c) : [...f.interests, c],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/international-professionals`, {
        full_name: form.full_name,
        email: form.email,
        country_of_residence: form.country_of_residence,
        role: form.role,
        regulator: form.regulator,
        registration_status: form.registration_status,
        countries_of_interest: form.interests.join(", "),
        experience_years: form.experience_years,
        preference: form.preference,
        website: form.website,
      });
      setSent(true);
      toast.success("Thank you — your interest has been registered.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="prof-success" className="bg-white border border-[#10B981]/30 rounded-[2rem] p-10 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Interest registered</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you, {form.full_name.split(" ")[0]}. Your details are on our international professionals
          register. Should a suitable opportunity arise in your countries of interest, we will be in touch.
        </p>
        <button data-testid="prof-new-button" onClick={() => { setSent(false); setForm(empty); }}
          className="mt-8 text-sm font-bold text-[#0B192C] border-b-2 border-[#10B981] pb-1 hover:text-[#10B981] transition-colors">
          Register another professional
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="professional-form"
      className="bg-white border border-[#E2E8F0] rounded-[2rem] p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="pf-name" className={labelCls}>Name *</label>
          <input id="pf-name" data-testid="prof-input-name" required minLength={2}
            value={form.full_name} onChange={upd("full_name")} placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pf-email" className={labelCls}>Email address *</label>
          <input id="pf-email" data-testid="prof-input-email" type="email" required
            value={form.email} onChange={upd("email")} placeholder="you@example.com" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pf-residence" className={labelCls}>Country of residence *</label>
          <input id="pf-residence" data-testid="prof-input-residence" required minLength={2}
            value={form.country_of_residence} onChange={upd("country_of_residence")} placeholder="United Kingdom" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pf-role" className={labelCls}>Professional role *</label>
          <select id="pf-role" data-testid="prof-select-role" value={form.role} onChange={upd("role")} className={inputCls}>
            {["Clinical Pharmacist", "Senior Clinical Pharmacist", "Independent Prescribing Pharmacist", "Pharmacy Technician", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="pf-regulator" className={labelCls}>Current professional regulator</label>
          <input id="pf-regulator" data-testid="prof-input-regulator"
            value={form.regulator} onChange={upd("regulator")} placeholder="e.g. GPhC, DHA, DHP" className={inputCls} />
        </div>
        <div>
          <label htmlFor="pf-regstatus" className={labelCls}>Registration / licensing status</label>
          <select id="pf-regstatus" data-testid="prof-select-status" value={form.registration_status} onChange={upd("registration_status")} className={inputCls}>
            {["Fully registered", "Provisionally registered", "Registration in progress", "Not currently registered"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <span className={labelCls}>Countries of interest</span>
          <div className="flex flex-wrap gap-2.5">
            {["United Arab Emirates", "Qatar", "Oman"].map((c) => {
              const active = form.interests.includes(c);
              return (
                <button type="button" key={c} data-testid={`prof-interest-${c === "United Arab Emirates" ? "uae" : c.toLowerCase()}`}
                  onClick={() => toggleInterest(c)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold border transition-colors duration-300 ${
                    active ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0B192C]"
                  }`}>
                  {active && <Check size={13} />} {c}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label htmlFor="pf-exp" className={labelCls}>Years of relevant experience</label>
          <select id="pf-exp" data-testid="prof-select-experience" value={form.experience_years} onChange={upd("experience_years")} className={inputCls}>
            {["0–2 years", "3–5 years", "6–10 years", "10+ years"].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="pf-pref" className={labelCls}>Remote or on-site preference</label>
          <select id="pf-pref" data-testid="prof-select-preference" value={form.preference} onChange={upd("preference")} className={inputCls}>
            {["Remote", "On-site", "Either"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="pf-web">Website</label>
          <input id="pf-web" data-testid="prof-honeypot" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={upd("website")} />
        </div>
      </div>
      <p className="mt-5 text-xs leading-relaxed text-[#64748B]">
        Your details are used only to discuss professional opportunities with NCS. Read our <PrivacyLink testid="prof-privacy-link" />.
      </p>
      <button type="submit" disabled={sending} data-testid="prof-submit-btn"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed">
        {sending ? (<>Submitting <Loader2 size={16} className="animate-spin" /></>)
          : (<>Register my interest <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
      </button>
    </form>
  );
};

/* --------------------------------- Page --------------------------------- */
const International = () => {
  const [partnerCountry, setPartnerCountry] = useState(null);
  const [partnerService, setPartnerService] = useState(null);
  const [calcSummary, setCalcSummary] = useState([]);

  const openPartner = ({ country, service }) => {
    if (country) setPartnerCountry(country);
    if (service) setPartnerService(service);
    scrollToId("partner");
  };

  return (
    <main data-testid="international-page">
      <Seo
        title="NCS International | Clinical Pharmacy Partnerships in the Middle East"
        description="National Clinical Services is exploring clinical pharmacy consultancy, medicines optimisation and pharmacist workforce partnerships in the UAE, Qatar and Oman."
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">
              <Globe2 size={14} /> NCS International — Middle East
            </p>
            <h1 data-testid="intl-hero-heading"
              className="mt-4 font-display font-extrabold tracking-tight leading-[1.06] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>NCS International</MaskedLine>
              <MaskedLine delay={0.3}>
                <span className="font-serif-accent">Expanding clinical expertise</span> across the Middle East
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-5 text-base sm:text-lg font-semibold text-[#0B192C]">
              UK-founded clinical expertise. International healthcare partnerships.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.62 }}
              className="mt-4 text-base leading-relaxed text-[#475569] max-w-2xl">
              National Clinical Services is exploring opportunities to extend its clinical pharmacy expertise
              and workforce solutions into the Middle East, with an initial focus on the United Arab Emirates,
              Qatar and Oman.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.72 }}
              className="mt-4 text-base leading-relaxed text-[#475569] max-w-2xl">
              Building on our experience supporting primary care in the United Kingdom, our international vision
              is to collaborate with healthcare organisations, clinics, hospitals and pharmacy providers to
              develop safe, efficient and professionally governed clinical pharmacy services. We welcome
              enquiries from organisations seeking clinical pharmacy consultancy, medicines optimisation
              programmes, workforce partnerships and appropriately licensed remote clinical support.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-8 flex flex-wrap gap-4">
              <button onClick={() => scrollToId("services")} data-testid="intl-hero-services-btn"
                className="group inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
                Explore International Services
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button onClick={() => scrollToId("partner")} data-testid="intl-hero-partner-btn"
                className="inline-flex items-center gap-2.5 text-[#0B192C] font-semibold px-7 py-4 rounded-full text-sm border border-[#0B192C]/25 transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
                Discuss a Partnership
              </button>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, rotate: 2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative">
              <img src="/assets/ncs-mark.png?v=2" alt="" aria-hidden="true"
                className="absolute -top-12 -left-12 w-40 opacity-20 -rotate-12 select-none pointer-events-none" />
              <div className="relative rounded-[2rem] overflow-hidden border-[6px] border-white shadow-[0_16px_40px_-12px_rgba(11,25,44,0.12)]">
                <img src="/assets/intl-hero.png" alt="Healthcare professionals in a modern Middle Eastern hospital with a city skyline behind them"
                  data-testid="intl-hero-image"
                  className="w-full h-[340px] sm:h-[440px] object-cover" />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }}
                className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-xl border border-[#E2E8F0] px-5 py-3.5">
                <p className="font-display text-sm font-bold text-[#0B192C]">UK-founded. Internationally minded.</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TARGET MARKETS */}
      <section id="markets" className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our target markets</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl mx-auto">
              Three markets. One standard of clinical governance.
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-2xl mx-auto">
              We are actively exploring partnerships in the UAE, Qatar and Oman — delivered only ever under
              the relevant local licensing and regulatory frameworks.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-7">
            {COUNTRIES.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.12}>
                <div data-testid={`country-card-${c.key}`}
                  className="h-full flex flex-col bg-[#FAFAFC] border border-[#E2E8F0] rounded-[2rem] overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <div className="relative">
                    <img src={c.image} alt={`${c.city} skyline, ${c.name}`} loading="lazy"
                      className="w-full h-48 object-cover" />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/90 backdrop-blur rounded-full pl-1.5 pr-3.5 py-1.5 shadow">
                      <img src={c.flag} alt={`${c.name} flag`} loading="lazy" className="w-6 h-6 rounded-full object-cover ring-1 ring-black/10" />
                      <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#0B192C]">{c.city}</span>
                    </span>
                  </div>
                  <div className="flex flex-col flex-1 p-7">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-extrabold" style={{ color: c.color }}>{c.name}</p>
                    <h3 className="mt-2 font-display text-xl font-bold text-[#0B192C] leading-snug">{c.title}</h3>
                    <p className="mt-3 text-sm text-[#475569]">{c.intro}</p>
                    <ul className="mt-4 space-y-2">
                      {c.services.map((s) => (
                        <li key={s} className="flex items-start gap-2.5 text-sm text-[#0B192C]">
                          <Check size={15} className="shrink-0 mt-0.5" style={{ color: c.color }} /> {s}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 bg-white border border-[#E2E8F0] rounded-2xl p-5">
                      <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-[#0B192C]">
                        <ShieldCheck size={14} style={{ color: c.color }} /> Regulatory information
                      </p>
                      <ul className="mt-3 space-y-2">
                        {c.regulatory.map((r) => (
                          <li key={r} className="text-xs leading-relaxed text-[#64748B]">{r}</li>
                        ))}
                      </ul>
                    </div>
                    <button onClick={() => openPartner({ country: c.formValue })} data-testid={`country-cta-${c.key}`}
                      className="group mt-6 inline-flex items-center justify-center gap-2.5 w-full bg-[#0B192C] text-white font-bold px-6 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] hover:text-[#0B192C]">
                      {c.cta}
                      <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">International service offerings</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl">
              What we can build with you
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <div data-testid={`service-card-${i + 1}`}
                  className="h-full flex flex-col bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${s.color}1A`, color: s.color }}>
                    <s.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#475569] flex-1">{s.text}</p>
                  <button onClick={() => openPartner({ service: s.formValue })} data-testid={`service-enquire-${i + 1}`}
                    className="group mt-5 inline-flex items-center gap-2 text-sm font-bold transition-colors duration-300"
                    style={{ color: s.color }}>
                    Enquire about this service
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">International workforce calculator</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl mx-auto">
              Estimate pharmacist hours and staffing for your project
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-2xl mx-auto">
              The same planning engine we use for UK primary care — tuned for international scoping.
              Indicative costs appear only where NCS has approved commercial rates for that country.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <IntlCalculator onAttach={setCalcSummary} />
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">How to work with NCS</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              A five-step partnership process
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div data-testid={`process-step-${s.n}`}
                  className="h-full bg-[#FAFAFC] border border-[#E2E8F0] rounded-3xl p-6 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span className="w-10 h-10 rounded-full bg-[#0B192C] text-white font-display font-extrabold flex items-center justify-center text-sm">
                    {s.n}
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold text-[#0B192C] leading-snug">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#475569]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP FORM */}
      <section id="partner" className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className="font-display text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">Let's talk</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Partner With NCS International
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-xl mx-auto">
              Completed enquiries go straight to our team — you'll receive an automatic acknowledgement
              and a personal response.
            </p>
          </Reveal>
          <Reveal>
            <PartnerForm country={partnerCountry} service={partnerService} calcSummary={calcSummary} />
          </Reveal>
        </div>
      </section>

      {/* PROFESSIONALS */}
      <section id="professionals" className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">International clinical professionals</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Pharmacists & pharmacy technicians: register your interest
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#475569]">
              If international clinical pharmacy work interests you — whether remote, on-site or a future
              relocation — join our professionals register and we'll contact you if a suitable, properly
              licensed opportunity emerges.
            </p>
            <div className="mt-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5">
              <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-[#0B192C]">
                <ShieldCheck size={14} className="text-[#10B981]" /> Please note
              </p>
              <p className="mt-2.5 text-xs leading-relaxed text-[#64748B]">
                This register records expressions of interest only. NCS does not currently advertise
                international vacancies, and registration does not guarantee any placement. Any future
                opportunity would be subject to local licensing, regulatory approval and contractual
                arrangements.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <ProfessionalForm />
          </Reveal>
        </div>
      </section>

      {/* COMPLIANCE BAND */}
      <section className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-[#0B192C] px-8 py-12 sm:px-14 text-center">
              <img src="/assets/ncs-mark.png" alt="" className="absolute -right-14 -bottom-16 w-64 opacity-15 rotate-12 pointer-events-none" />
              <div className="relative max-w-3xl mx-auto">
                <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">
                  <ShieldCheck size={14} /> Our regulatory commitment
                </p>
                <p className="mt-5 text-base sm:text-lg leading-relaxed text-white/75">
                  All international services are subject to local regulatory approval, professional licensing,
                  healthcare facility authorisation, contractual arrangements and operational feasibility.
                  NCS works with appropriately licensed professionals and local partners — we do not deliver
                  patient-facing services in any jurisdiction until the applicable requirements are met.
                </p>
                <button onClick={() => scrollToId("partner")} data-testid="compliance-cta-btn"
                  className="group mt-8 inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-white">
                  Start the conversation
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default International;
